import React from "react";
import { useState } from "react";
import * as yup from "yup";
import { TextField } from "@mui/material";
import { useFormik } from "formik";
import { BiCheckCircle } from "react-icons/bi";
import { RiUserSettingsLine } from "react-icons/ri";
import { HashLoader } from "react-spinners";
import { styled } from "@mui/material/styles";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { AiOutlineInfoCircle } from "react-icons/ai";
import Typography from "@mui/material/Typography";

import { MainButton } from "../../components/MainButton";
import { COLORS } from "../../utils/colors";
import { createOperatorStep2 } from "../../api/operators";
import { getOrgByEmail } from "../../api/user";
import { useDispatch } from "react-redux";
import { updateUser } from "../../store/mainReducer";

const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} placement="left" />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: COLORS.veryLightMainAccent,
    color: "rgba(0, 0, 0, 0.87)",
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(12),
    border: "1px solid #dadde9",
  },
}));

const validationSchema = yup.object({
  name: yup
    .string("Enter the operator name")
    .required("Operator name is required"),
});

export default function Step2({
  step1Completed,
  step2Completed,
  setStep2Completed,
  userData,
}) {
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  const formik = useFormik({
    initialValues: {
      name: "Default operator",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => handleFormSubmit(values),
  });

  const handleFormSubmit = async (values) => {
    setLoading(true);

    const response = await createOperatorStep2({
      name: values.name,
      organization_id: userData._id,
    });

    if (response.status === 200) {
      setStep2Completed(true);

      const { data } = await getOrgByEmail(userData.email);
      dispatch(updateUser(data));
    }

    setLoading(false);
  };

  return (
    <div
      className="standard-container"
      style={{
        opacity: (!step1Completed || step2Completed) && 0.5,
        borderColor: step2Completed && COLORS.mainAccent,
      }}
    >
      <h2>
        Step 2/2 - Create your first operator
        {step2Completed && <BiCheckCircle color={COLORS.mainAccent} />}
      </h2>
      <p id="subTitle" style={{ marginBottom: "20px" }}>
        Operators are the main way of managing your ticket inventory - they have
        API keys & through operators, your business partners have direct access
        to your inventory. All under your control.
      </p>
      <form className="onboarding-form">
        <HtmlTooltip
          title={
            <React.Fragment>
              <Typography color="inherit">
                Default Operator - Definition
              </Typography>
              {"The"} <b>{"default operator"}</b>{" "}
              {
                " is the main way of your organization to interact with the OOVAL infrastructure. It cannot be deleted."
              }
            </React.Fragment>
          }
        >
          <p
            id="subheaderBlack"
            style={{
              margin: "0px 0px 6px",
              alignSelf: "center",
              alignItems: "center",
              display: "flex",
              gap: "5px",
            }}
          >
            Operator name
            <AiOutlineInfoCircle />
          </p>
        </HtmlTooltip>
        <TextField
          fullWidth
          disabled={loading || !step1Completed || step2Completed}
          size="small"
          className="input"
          name={"name"}
          value={"Default operator"}
          onChange={formik.handleChange}
          error={formik.touched.name && Boolean(formik.errors.name)}
          helperText={formik.touched.name && formik.errors.name}
          sx={{
            "& label": { paddingLeft: (theme) => theme.spacing(1) },
            "& fieldset": {
              borderRadius: "6px",
              border: `2px solid #f0f1f5`,
              transition: ".2s ease-in-out all",
            },
            "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
              {
                borderColor: "#f0f1f5",
              },
            "& .MuiOutlinedInput-input:hover": {
              cursor: "not-allowed",
            },
          }}
        />
        <div className="field-container"></div>
        <MainButton
          text={"Create operator"}
          disabled={loading || !step1Completed || step2Completed}
          color={COLORS.black}
          hoverColor={COLORS.black}
          textColor={COLORS.white}
          borderColor={COLORS.grey}
          shadowColor={COLORS.mainAccent}
          textHoverColor={COLORS.lightGrey}
          onClickAction={() => formik.handleSubmit()}
          width={"fit-content"}
        >
          {loading ? (
            <HashLoader size={20} color={COLORS.white} />
          ) : step2Completed ? (
            <BiCheckCircle size={20} color={COLORS.mainAccent} />
          ) : (
            <RiUserSettingsLine size={20} />
          )}
        </MainButton>
      </form>
    </div>
  );
}
